import React, { useState } from 'react';
import { TrendingUp, ChevronDown } from 'lucide-react';
import { getCustomRates, getSalaryTypeLabel, SalaryTypeKey } from '../services/customRatesService';
import { CONCEPT_SECTIONS, DEFAULT_RATES } from '../utils/constants';
import styles from './OverheadAnalysisCard.module.css';

interface Props {
    salaryType: SalaryTypeKey;
    smmlv: number;
    gaPercentage: number;
    baseSalary?: number;
}

export function OverheadAnalysisCard({ salaryType, smmlv, gaPercentage, baseSalary }: Props) {
    // Default collapsed as requested
    const [isExpanded, setIsExpanded] = useState(false);

    // Calculate effective rates (default + custom)
    const effectiveRates = getCustomRates(salaryType);

    // Helper to interpret rates similar to ConfigView
    const getRateValue = (conceptName: string): number => {
        // Fallback to DEFAULT_RATES if not in custom/effective rates
        const val = effectiveRates[conceptName];
        if (val !== undefined) return val;
        return DEFAULT_RATES[conceptName] ?? 0;
    };

    // Use baseSalary if available, otherwise fallback to smmlv
    const calculationBase = baseSalary && baseSalary > 0 ? baseSalary : smmlv;
    const baseLabel = baseSalary && baseSalary > 0 ? "Salario Base:" : "1 SMMLV:";

    return (
        <div className={styles.factorCard}>
            <div
                className={styles.cardHeader}
                onClick={() => setIsExpanded(!isExpanded)}
                style={{ cursor: 'pointer', marginBottom: isExpanded ? '20px' : '0' }}
            >
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <TrendingUp size={20} color="#10b981" />
                    <h3 className={styles.cardTitle}>Análisis de Sobrecosto</h3>
                </div>
                <ChevronDown
                    size={20}
                    color="white"
                    style={{
                        marginLeft: 'auto',
                        transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.2s ease'
                    }}
                />
            </div>

            {isExpanded && (
                <>
                    <div className={styles.factorHeader}>
                        <div className={styles.labelWithTooltip}>
                            <span className={styles.factorLabel}>Factor Multiplicador Est.</span>
                            <div className={styles.tooltipContainer}>
                                <div className={styles.infoIcon}>i</div>
                                <div className={styles.tooltipContent}>
                                    <p><strong>¿Qué es este factor?</strong></p>
                                    <p>Indica el costo real por cada peso de salario.</p>
                                    <p className={styles.tooltipNote}>Este factor es una estimación basada en los porcentajes configurados y el G&A. No incluye valores fijos (como seguros) ni costos operativos únicos.</p>
                                    <p style={{ marginTop: '4px', fontSize: '10px', opacity: 0.8 }}>Fórm: (1 + Carga Prestacional) * (1 + G&A)</p>
                                </div>
                            </div>
                        </div>
                        <span className={styles.factorValue} style={{ color: '#ffffff', textShadow: '0 0 20px rgba(255, 255, 255, 0.3)' }}>
                            {(() => {
                                let totalPercentage = 0;
                                const relevantSections = ['Prestaciones Sociales', 'Seguridad Social', 'Parafiscales', 'Otros Beneficios'];

                                CONCEPT_SECTIONS.forEach(section => {
                                    if (relevantSections.includes(section.title)) {
                                        section.concepts.forEach(concept => {
                                            if (!concept.isFixed && !concept.isFixedUSD) {
                                                const val = getRateValue(concept.name);
                                                totalPercentage += val;
                                            }
                                        });
                                    }
                                });

                                // If totalPercentage is sum of decimals (e.g. 0.52), then:
                                const baseFactor = 1 + totalPercentage;
                                const totalFactor = baseFactor * (1 + (gaPercentage / 100));

                                return `${totalFactor.toFixed(2)}x`;
                            })()}
                        </span>
                    </div>

                    <div className={styles.factorExplanation}>
                        <p>Para el <strong>{getSalaryTypeLabel(salaryType)}</strong>:</p>
                        <div className={styles.factorExample}>
                            <span className={styles.exampleLabel}>{baseLabel}</span>
                            <strong className={styles.exampleValue}>$ {calculationBase.toLocaleString('es-CO')}</strong>
                        </div>
                        <div className={styles.factorExample}>
                            <span className={styles.exampleLabel}>Costo Aprox:</span>
                            <strong className={styles.exampleTotal}>
                                {(() => {
                                    let totalPercentage = 0;
                                    const relevantSections = ['Prestaciones Sociales', 'Seguridad Social', 'Parafiscales', 'Otros Beneficios'];
                                    CONCEPT_SECTIONS.forEach(section => {
                                        if (relevantSections.includes(section.title)) {
                                            section.concepts.forEach(concept => {
                                                if (!concept.isFixed && !concept.isFixedUSD) {
                                                    const val = getRateValue(concept.name);
                                                    totalPercentage += val;
                                                }
                                            });
                                        }
                                    });
                                    const baseFactor = 1 + totalPercentage;
                                    const totalFactor = baseFactor * (1 + (gaPercentage / 100));
                                    return `$ ${(calculationBase * totalFactor).toLocaleString('es-CO', { maximumFractionDigits: 0 })}`;
                                })()}
                            </strong>
                        </div>
                    </div>


                </>
            )}
        </div>
    );
}
